import { Error as ErrorNotice } from "@ui/error/Error";
import { FC, useEffect, useState } from "react";
import { Avatar, DS } from "../ds/Kit";
import { GD } from "src/GD";
import { SignalHandler } from "badmfck-signal";
import { useRouter } from "src/services/Router";
import { StatusBadge } from "./StatusBadge";
import { Box, HBox, VBox } from "@ui/box/Box";
import { Typo } from "@ui/text/Typo";
import { IconInfo } from "@ui/icons/IconInfo";
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter";
import { useWindowSize } from "@ui/utils/useResize";
import { DEFAULT_CURRENCY } from "src/constants";
import { useIssuer } from "../issuer/IssuerProvider";
import { findSort, sortQuery, sortRows, textMatches, type SortOption } from "src/utils/listSort";
import { usePagedList } from "../hooks/usePagedList";
import { ListPager } from "./ListPager";

/** Sum of the team's buyer balances on the selected issuer, as teams/list reports it. */
export const getTeamBalance = (team: ITeamSaved) => Number(team.balance ?? 0) || 0;

export const TEAM_SORT_OPTIONS: SortOption<ITeamSaved>[] = [
  { label: "Newest first", sort: "created_at", order: "desc", get: (t) => t.created_at },
  { label: "Name A–Z", sort: "name", order: "asc", get: (t) => t.name },
  { label: "Most buyers", sort: "buyers_count", order: "desc", get: (t) => t.buyers_count ?? 0 },
  { label: "Balance: high to low", sort: "balance", order: "desc", get: getTeamBalance },
  { label: "Status", sort: "status", order: "asc", get: (t) => t.status },
];

const TEAM_PAGE_SIZE = 24;

const summarizeTeams = (teams: ITeamSaved[]): ITeamListSummary => ({
  teams: teams.length,
  active: teams.filter((team) => team.status === "active").length,
  buyers: teams.reduce((sum, team) => sum + (team.buyers_count ?? 0), 0),
  balance: teams.reduce((sum, team) => sum + getTeamBalance(team), 0),
});

const TEAM_LIST_PANEL_BORDER = "#E3E7EF";
const TEAM_LIST_BRAND = "#2B5C8A";
const TEAM_LIST_MUTED = "#617087";

type TeamsListFilter = {
  name: string;
  sort?: string;
};

export const TeamsList: FC<{
  filter: TeamsListFilter;
  /** Totals of every team that matches the filters, not just the page. */
  onSummary?: (summary: ITeamListSummary | null) => void;
}> = ({ filter, onSummary }) => {
  const { width: viewportWidth } = useWindowSize();
  const { selectedIssuer, selectedIssuerId } = useIssuer();
  const issuerCurrency = selectedIssuer?.currency || DEFAULT_CURRENCY;
  const isMobile = viewportWidth <= 768;
  const router = useRouter();

  const [debouncedFilter, setDebouncedFilter] = useState(filter);

  useEffect(() => {
    const delay = 500;
    const timeout = setTimeout(() => {
      setDebouncedFilter(filter);
    }, delay);

    return () => clearTimeout(timeout);
  }, [filter]);

  const search = debouncedFilter.name.trim();
  const sort = findSort(TEAM_SORT_OPTIONS, debouncedFilter.sort);
  const list = usePagedList<ITeamSaved, ITeamListSummary, IListQuery>({
    request: (query) => GD.REQ_TEAMS_PAGE.request(query),
    query: {
      ...(selectedIssuerId === null ? {} : { issuer: selectedIssuerId }),
      ...(search ? { search } : {}),
      ...sortQuery(sort),
    },
    pageSize: TEAM_PAGE_SIZE,
    legacy: (teams) => sortRows(teams.filter((team) => textMatches(search, [team.name, team.uid])), sort),
    summarize: summarizeTeams,
  });
  const { reload } = list;

  useEffect(() => {
    onSummary?.(list.summary);
  }, [list.summary, onSummary]);

  useEffect(() => {
    const sh = new SignalHandler();
    // Teams or balances changed elsewhere; the payload is the whole list, so read this page again.
    sh.add(GD.S_TEAMS_LIST_UPDATED, () => void reload());

    return () => {
      sh.clear();
    };
  }, [reload]);

  if (!list.loading && !list.error && list.rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px",
          padding: "60px",
        }}
      >
        <Box
          sx={{
            color: TEAM_LIST_BRAND,
            backgroundColor: "#FBFCFD",
            border: `1px solid ${TEAM_LIST_PANEL_BORDER}`,
            borderRadius: "12px",
            padding: "10px",
            display: "flex",
          }}
        >
          <IconInfo width={28} height={28} color={TEAM_LIST_BRAND} />
        </Box>
        <Typo variant="medium" color={TEAM_LIST_MUTED}>
          No teams found
        </Typo>
      </Box>
    );
  }

  if (list.error && !list.rows.length) {
    return <ErrorNotice error={list.error} sx={{ margin: "16px" }} />;
  }

  if (list.initialLoading) {
    return (
      <VBox center gap="6px" sx={{ minHeight: "180px", padding: "24px" }}>
        <Typo sx={{ color: DS.muted, fontSize: "13px" }}>Loading teams…</Typo>
      </VBox>
    );
  }

  return (
    <VBox aria-busy={list.loading} sx={{ height: isMobile ? "auto" : "100%", minHeight: 0 }}>
    <Box
      sx={{
        flex: isMobile ? "none" : 1,
        minHeight: 0,
        opacity: list.loading ? 0.6 : 1,
        transition: "opacity .15s ease",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        gap: "14px",
        padding: isMobile ? "12px" : "16px",
        overflowY: "auto",
        alignContent: "start",
      }}
    >
      {list.rows.map((team: ITeamSaved) => {
        const balance = getTeamBalance(team);
        const buyers = team.buyers_count || 0;
        return (
          <Box
            key={team.uid}
            role="button"
            tabIndex={0}
            aria-label={`Open team ${team.name}`}
            onClick={() => router.navigate("team_details", { teamId: team.uid })}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ")
                router.navigate("team_details", { teamId: team.uid });
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              padding: "18px",
              borderRadius: DS.radius,
              border: `1px solid ${DS.line}`,
              background: DS.surface,
              boxShadow: DS.shadow,
              cursor: "pointer",
              transition: "border-color .15s ease, box-shadow .15s ease, transform .15s ease",
              "&:hover": {
                borderColor: "#B9CDE2",
                boxShadow: "0 10px 24px rgba(30, 58, 95, 0.10)",
                transform: "translateY(-1px)",
              },
              "&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "2px" },
            }}
          >
            <HBox sx={{ alignItems: "center", gap: "12px", minWidth: 0 }}>
              <Avatar name={team.name} size={40} />
              <VBox gap="2px" sx={{ minWidth: 0, flex: 1 }}>
                <Typo sx={{ fontSize: "15px", fontWeight: 650, color: DS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {team.name}
                </Typo>
                <Typo sx={{ fontSize: "12px", color: DS.muted }}>
                  {`Created ${new Date(team.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
                </Typo>
              </VBox>
              <StatusBadge status={team.status || "active"} />
            </HBox>
            <HBox sx={{ gap: "0", borderRadius: "12px", background: DS.soft, padding: "12px 14px" }}>
              <VBox gap="2px" sx={{ flex: 1 }}>
                <Typo sx={{ fontSize: "12px", color: DS.muted }}>Buyer balance</Typo>
                <Typo sx={{ fontSize: "18px", fontWeight: 700, color: DS.ink, fontVariantNumeric: "tabular-nums" }}>
                  {formatCurrencyWithNumberToSymbolWithNumber(balance, issuerCurrency)}
                </Typo>
              </VBox>
              <VBox gap="2px" sx={{ paddingLeft: "14px", borderLeft: `1px solid ${DS.line}` }}>
                <Typo sx={{ fontSize: "12px", color: DS.muted }}>Buyers</Typo>
                <Typo sx={{ fontSize: "18px", fontWeight: 700, color: DS.ink }}>{buyers}</Typo>
              </VBox>
            </HBox>
            <HBox sx={{ alignItems: "center", justifyContent: "space-between" }}>
              <Typo sx={{ fontSize: "12px", color: DS.faint, fontFamily: "ui-monospace, monospace" }}>{team.uid}</Typo>
              <Typo sx={{ fontSize: "13px", fontWeight: 600, color: DS.accent }}>Open team →</Typo>
            </HBox>
          </Box>
        );
      })}
    </Box>
    <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} label="Teams" />
    </VBox>
  );
};
